import React, {
  Fragment,
  useState,
  type ComponentType,
  type PropsWithChildren,
  type ReactElement,
} from 'react';
import styled from 'styled-components';

import { Modal } from 'components/styled-primitives';
import { fjModalSizes } from 'components/primitives';
import {
  DataList,
  DataListHeader,
  ModalHeader,
  ModalSection as ModalSectionUntyped,
  ModalSectionWrapper as ModalSectionWrapperUntyped,
  UserFieldsEditor,
} from 'components/modal-components';

// styled-components in modal-components use custom props via interpolation that
// its JS-inferred types don't expose; re-type them to accept those props.
const ModalSection = ModalSectionUntyped as ComponentType<PropsWithChildren<{ flex?: number }>>;
const ModalSectionWrapper = ModalSectionWrapperUntyped as ComponentType<
  PropsWithChildren<{ direction?: string }>
>;
import { formatDateTime, getSuperAdminLicenseStatus } from 'utils';

import FjSortTable from 'components/table/fj-sort-table';
import { CostCenterCell } from 'components/table/table-cells';

interface ProductAccess {
  name: string;
  type: string;
  activityState: string;
  startDate?: string;
  endDate?: string;
  sortEndDate: string;
  status?: string;
  users?:
    | Record<string, { endDate?: string | null; startDate?: string | null; suspended?: boolean } | undefined>
    | undefined;
}

interface CostCenter {
  name: string;
  id: string;
  primaryEmail: string;
  owner: string;
  type?: string;
  productAccess: ProductAccess[];
}

interface License extends ProductAccess {
  costCenter: CostCenter;
}

interface SuperAdminUser {
  id: string;
  firstname: string;
  lastname: string;
  primaryEmail: string;
  accountState: string;
  isVerified: boolean;
  isPhantom: boolean;
  country?: string;
  countryCode?: string;
  createdAt: string;
  deletedDate: string;
  // UserFieldsEditor's UserFields shape carries an index signature; mirror it so
  // this user is assignable to that prop.
  [key: string]: unknown;
}

interface CostCenterCellValue {
  costCenter: string;
  email: string;
  id: string;
}

interface UserDetailModalProps {
  user: SuperAdminUser;
  onRequestClose: () => void;
  userCCs?: Record<string, CostCenter>;
  setSelectedLicense: (license: License) => void;
}

// `overflow: hidden` (not `auto`): FjSortTable scrolls its own body, so an
// `auto` here would add a second scrollbar that drags the whole table —
// header included — out from under the one that scrolls the rows.
const TableBorder = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid black;
  overflow: hidden;
`;

const UserDetailModal = ({
  user,
  onRequestClose,
  userCCs = {},
  setSelectedLicense,
}: UserDetailModalProps): ReactElement => {
  const [currentUser, setCurrentUser] = useState<SuperAdminUser>(user);

  React.useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const licenses = Object.values(userCCs).reduce<License[]>(
    (acc, costCenter) => [...acc, ...costCenter.productAccess.map((pa) => ({ ...pa, costCenter }))],
    [],
  );

  const columns = [
    {
      id: 'costCenter',
      path: ({ costCenter: { name, id, primaryEmail } }: License): CostCenterCellValue => ({
        costCenter: name,
        email: primaryEmail,
        id,
      }),
      Header: (): string => 'Cost Center',
      Cell: (
        { value, original }: { value: CostCenterCellValue; original: License },
        props: Record<string, unknown>,
      ): ReactElement => (
        <CostCenterCell {...value} onClick={() => setSelectedLicense(original)} {...props} />
      ),
      CellContainer: Fragment,
      alignLeft: true,
      minWidth: 200,
    },
    {
      id: 'isAdmin',
      path: (license: License): string =>
        license.costCenter?.owner === currentUser.id ? 'Yes (Owner)' : 'No',
      Header: (): string => 'Admin',
      minWidth: 80,
    },
    {
      path: 'name',
      Header: (): string => 'Product',
      minWidth: 80,
    },
    {
      path: 'type',
      Header: (): string => 'Type',
      minWidth: 80,
    },
    {
      id: 'status',
      path: (license: License): string =>
        getSuperAdminLicenseStatus(
          license.costCenter?.type,
          license,
          { userSeat: true, userId: currentUser.id },
        ),
      Header: (): string => 'Status',
      minWidth: 80,
    },
    {
      path: 'activityState',
      Header: (): string => 'Activity State',
      minWidth: 80,
    },
    {
      id: 'licenseEndDate',
      path: (license: License): string => formatDateTime(license.sortEndDate),
      Header: (): string => 'License End Date',
      minWidth: 150,
    },
    {
      id: 'seatEndDate',
      path: (license: License): string =>
        formatDateTime(license.users?.[currentUser.id]?.endDate) || 'N/A',
      Header: (): string => 'Seat End Date',
      minWidth: 120,
    },
  ];

  return (
    <Modal
      accept={{
        text: 'OK',
        callback: onRequestClose,
      }}
      size={fjModalSizes.XL}
      showX
      onClickX={onRequestClose}
    >
      <ModalSectionWrapper direction="column">
        <ModalHeader>{`${currentUser.firstname} ${currentUser.lastname}`}</ModalHeader>
        <ModalSection flex={0}>
          <DataListHeader>User Information</DataListHeader>
          <DataList
            data={[
              ['Email', currentUser.primaryEmail],
              ['ID', currentUser.id],
              ['Account Status', currentUser.accountState],
              ['Email Verified', currentUser.isVerified],
              ['Phantom', currentUser.isPhantom],
              ['Country', currentUser.country || currentUser.countryCode],
              ['Created', formatDateTime(currentUser.createdAt)],
              ['Deleted', formatDateTime(currentUser.deletedDate)],
              [
                'DataDog',
                <a
                  href={`https://app.datadoghq.com/logs?query=${encodeURIComponent(
                    `@additionalInfo.userEmail:${currentUser.primaryEmail} OR @uid:${currentUser.id} OR @userId:${currentUser.id}`,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Logs (requires DataDog account access)
                </a>,
              ],
            ]}
          />
        </ModalSection>
        <ModalSection flex={0}>
          <DataListHeader>Edit User Fields</DataListHeader>
          <UserFieldsEditor
            user={currentUser}
            onUserUpdate={(updated) => setCurrentUser(updated as SuperAdminUser)}
          />
        </ModalSection>
        <ModalSection>
          <DataListHeader>Licenses</DataListHeader>
          <TableBorder>
            <FjSortTable
              key={licenses.length}
              data={licenses}
              columns={columns}
              defaultPageSize={licenses.length}
              hidePagination
              tableName="license-detail-modal"
            />
          </TableBorder>
        </ModalSection>
      </ModalSectionWrapper>
    </Modal>
  );
};

export default UserDetailModal;
